import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput, formatLatexPreview } from "../../../../components/inputs/FractionInput";
import EquationInput from "../../../../components/inputs/EquationInput"; // u góry pliku
import { parseLineEquation, equivalentLines } from "../../../../utils/lineEquation";

export function parseGeneralFormEquation(input) {
  return parseLineEquation(input, { form: "general" });
}

function gcd(a, b) {
  a = Math.round(a);
  b = Math.round(b);
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return Math.abs(a);
}

function formatFraction(numerator, denominator) {
  if (denominator === 0 || !Number.isFinite(denominator)) return "undefined";

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}



export function generateTask() {
  const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  let A, B, C;
  do {
    A = rand(-5, 5);
    B = rand(-5, 5);
  } while (A === 0 || B === 0); // aby współczynnik a był zdefiniowany

  C = rand(-10, 10);

  // a = -A/B, b = -C/B
  const a = -A / B;
  const b = -C / B;

  return {
    type: "ConvertSlopeToGeneralTask",
    data: { a, b, A, B, C }
  };
}


export function renderTask({ data }) {
  const { A, B, C } = data;

  const aLatexRaw = formatFraction(-A, B);
  const bLatexRaw = formatFraction(-C, B);

  const ax =
    aLatexRaw === "1" ? "x" :
    aLatexRaw === "-1" ? "-x" :
    `${aLatexRaw}x`;

  const bLatex = bLatexRaw === "0" ? "" :
    bLatexRaw.startsWith("-") ? ` - ${bLatexRaw.slice(1)}` :
    ` + ${bLatexRaw}`;

  return (
    <p>
      Przekształć równanie prostej <InlineMath math={`y = ${ax}${bLatex}`} /> do postaci ogólnej <InlineMath math="Ax + By + C = 0" />.
    </p>
  );
}


export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <>
      <EquationInput
        value={userAnswer || ""}
        onChange={(val) => onChange(val)}
        disabled={disabled}
        placeholder="np. (-1/2)x - y - 1 = 0"
      />
      <small style={{ display: "block", marginTop: 6, opacity: 0.75 }}>
        Ułamkowy współczynnik przy x możesz zapisać np. jako (-1/2)x albo -x/2.
      </small>
    </>
  );
}


export function checkAnswer({ data }, userAnswer) {
  return equivalentLines(parseGeneralFormEquation(userAnswer), data);
}


export function renderAnswer({ data }) {
  let { A, B, C } = data;

  const gcd = (a, b) => {
    while (b !== 0) [a, b] = [b, a % b];
    return Math.abs(a);
  };
  const gcd3 = (a, b, c) => gcd(gcd(a, b), c);

  const divisor = gcd3(A, B, C);
  if (divisor > 1) {
    A /= divisor;
    B /= divisor;
    C /= divisor;
  }

  const formatTerm = (coef, variable) => {
    if (coef === 0) return null;
    if (coef === 1) return `${variable}`;
    if (coef === -1) return `-${variable}`;
    return `${formatFraction(coef, 1)}${variable}`;
  };

  const parts = [
    formatTerm(A, "x"),
    formatTerm(B, "y"),
    C !== 0 ? formatFraction(C, 1) : null,
  ].filter(Boolean);

  const equation = parts.join(" + ").replace(/\+\s\-/g, "- ");

  return (
    <div className="space-y-2" style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <div>
        <div style={{ marginTop: "0.5rem" }}>
          <InlineMath math={`\\displaystyle ${equation} = 0`} />
        </div>
      </div>
    </div>
  );
}



export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "string" || userAnswer.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <InlineMath math={userAnswer} />
  );
}


export function renderExplanation({ data }) {
  let { A, B, C } = data;

  const aLatex = formatFraction(-A, B);
  const bLatex = formatFraction(-C, B);

  const ax = aLatex === "1" ? "x" : aLatex === "-1" ? "-x" : `${aLatex}x`;
  const bSign = bLatex.startsWith("-") ? `- ${bLatex.slice(1)}` : bLatex === "0" ? "" : `+ ${bLatex}`;
  const slopeForm = `y = ${ax}${bSign}`;

  // === Krok 2: logiczne przeniesienie ===
  let ax2;
  if (aLatex.startsWith("-")) {
    ax2 = `${aLatex.slice(1)}x`; // np. -1/2 → 1/2x
  } else {
    ax2 = `- ${aLatex}x`;        // np. 2/3 → - 2/3x
  }

  const b2 = bLatex === "0" ? "" : ` - ${bLatex}`;
  const step2 = `${ax2} + y${b2} = 0`;

  // === Krok 3: wynik końcowy z redukcją przez NWD ===
  const gcd = (a, b) => {
    while (b !== 0) [a, b] = [b, a % b];
    return Math.abs(a);
  };
  const gcd3 = (a, b, c) => gcd(gcd(a, b), c);
  const d = gcd3(A, B, C);
  const Ar = A / d;
  const Br = B / d;
  const Cr = C / d;

  const formatTerm = (coef, variable) => {
    if (coef === 0) return null;
    if (coef === 1) return `${variable}`;
    if (coef === -1) return `-${variable}`;
    return `${formatFraction(coef, 1)}${variable}`;
  };

  const partsFinal = [
    formatTerm(Ar, "x"),
    formatTerm(Br, "y"),
    Cr !== 0 ? formatFraction(Cr, 1) : null,
  ].filter(Boolean);
  const final = partsFinal.join(" + ").replace(/\+\s\-/g, "- ");

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Zaczynamy od postaci kierunkowej:
        <BlockMath math={slopeForm} />
      </div>

      <div>
        <strong>Krok 2:</strong> Przenosimy wszystkie wyrazy na lewą stronę:
        <BlockMath math={step2} />
      </div>

<div>
  <strong>Krok 3:</strong> Doprowadzamy równanie do postaci ogólnej z całkowitymi współczynnikami:
  <BlockMath math={`${final} = 0`} />
</div>

    </div>
  );
}



